import React from 'react';
import { Link } from 'react-router-dom';
import InteractiveMascot from '../components/InteractiveMascot';

// Get user from localStorage and check subscription status
const getCurrentUser = () => {
  try {
    const savedUser = localStorage.getItem('user');
    if (savedUser && savedUser !== 'undefined' && savedUser !== 'null') {
      const userData = JSON.parse(savedUser);
      if (userData && userData.id && userData.id !== -1) {
        // Check if subscription is expired
        if (userData.subscription && userData.subscription.end_date) {
          const endDate = new Date(userData.subscription.end_date);
          const now = new Date();
          if (now > endDate) {
            // Subscription expired, update user data
            userData.subscription.status = 'expired';
            localStorage.setItem('user', JSON.stringify(userData));
          }
        }
        return userData;
      }
    }
  } catch (error) {
    console.error('Error getting current user:', error);
  }
  return { id: -1, username: "Guest" };
};

const hasActiveSubscription = (user) => {
  if (!user || user.id === -1) return false;
  
  // Admin (role 3) always has access
  const userRole = parseInt(user?.role) || 0;
  if (userRole === 3) return true;
  
  // TEMPORARY: Force unlock for testuser@example.com for debugging
  const isTestUser = user?.email === 'testuser@example.com' || user?.username === 'testuser@example.com';
  if (isTestUser) return true;
  
  // Check subscription status - must be active and not cancelled
  const hasValidSubscription = user.subscription && 
    (user.subscription.status === 'active' || user.subscription.status === 1) &&
    user.subscription.status !== 'cancelled' &&
    (!user.subscription.end_date || new Date() <= new Date(user.subscription.end_date));
  
  // For Premium users (role 2), check if subscription is cancelled
  const isPremiumUser = userRole === 2;
  if (isPremiumUser) {
    // Premium users are blocked if subscription is cancelled
    const isSubscriptionCancelled = user.subscription && 
      (user.subscription.status === 'cancelled' || user.subscription.status === 3);
    if (isSubscriptionCancelled) return false;
    
    // Premium users have access if subscription is active OR not cancelled
    return hasValidSubscription || !isSubscriptionCancelled;
  }
  
  // For other roles (0, 1), only valid subscription grants access
  return hasValidSubscription;
};

// Class 5 Math Games
const CLASS_5_GAMES = [
  { 
    id: 'angle-adventure', 
    title: 'Angle Adventure', 
    description: 'Master lines, angles, and their properties!', 
    icon: '📐', 
    difficulty: 'Medium',
    topics: ['Lines', 'Angles', 'Geometry']
  },
  { 
    id: 'percentage-power', 
    title: 'Percentage Power', 
    description: 'Calculate percentages and conversions!', 
    icon: '%', 
    difficulty: 'Medium',
    topics: ['Percentage', 'Conversion']
  },
  { 
    id: 'algebra-quest', 
    title: 'Algebra Quest', 
    description: 'Solve algebraic expressions and equations!', 
    icon: '🧮', 
    difficulty: 'Hard',
    topics: ['Algebra', 'Equations', 'Variables']
  },
  { 
    id: 'volume-vault', 
    title: 'Volume Vault', 
    description: 'Calculate volume of 3D shapes!', 
    icon: '📦', 
    difficulty: 'Hard',
    topics: ['Volume', '3D Shapes', 'Measurement']
  },
  { 
    id: 'decimal-dragons', 
    title: 'Decimal Dragons', 
    description: 'Master decimal operations!', 
    icon: '🐉', 
    difficulty: 'Medium',
    topics: ['Decimals', 'Operations']
  },
  { 
    id: 'fraction-pizza', 
    title: 'Fraction Pizza', 
    description: 'Learn fractions and operations!', 
    icon: '🍕', 
    difficulty: 'Medium',
    topics: ['Fractions', 'Operations']
  },
  { 
    id: 'treasure-hunt-math', 
    title: 'Treasure Hunt Math', 
    description: 'Solve math clues to find hidden treasures on the island!', 
    icon: '🗺️', 
    difficulty: 'Medium',
    topics: ['Coordinates', 'Problem Solving', 'Mixed Operations']
  },
  { 
    id: 'math-shooter-pro', 
    title: 'Math Shooter Pro', 
    description: 'Shoot enemies with correct answers in this action game!', 
    icon: '🎯', 
    difficulty: 'Hard',
    topics: ['Quick Math', 'All Operations', 'Speed']
  },
  { 
    id: 'speed-racer-math', 
    title: 'Speed Racer Math', 
    description: 'Race your car and collect correct answers at high speed!', 
    icon: '🏎️', 
    difficulty: 'Medium',
    topics: ['Multiplication', 'Division', 'Fractions']
  },
  { 
    id: 'math-ninja-warrior', 
    title: 'Math Ninja Warrior', 
    description: 'Defeat enemies by solving math problems as a ninja!', 
    icon: '🥷', 
    difficulty: 'Hard',
    topics: ['Combat Math', 'Mixed Operations', 'Speed']
  },
  { 
    id: 'rocket-math-race', 
    title: 'Rocket Math Race', 
    description: 'Blast off to space by answering math questions!', 
    icon: '🚀', 
    difficulty: 'Medium',
    topics: ['Space Math', 'All Operations', 'Quick Thinking']
  },
];

// General Games - Available for All Users (Unlimited Play)
const GENERAL_GAMES = [
  { 
    id: 'word-puzzle-master', 
    title: 'Word Puzzle Master', 
    description: 'Unscramble letters to create words! Unlimited levels.', 
    icon: '🔤', 
    difficulty: 'Easy',
    topics: ['Vocabulary', 'Spelling', 'Word Formation'],
    unlimited: true
  },
  { 
    id: 'memory-match-math', 
    title: 'Memory Match Math', 
    description: 'Match math problems with their answers! Memory game.', 
    icon: '🧠', 
    difficulty: 'Medium',
    topics: ['Memory', 'Quick Math', 'Matching'],
    unlimited: true
  },
  { 
    id: 'puzzle-unlock', 
    title: 'Puzzle Unlock', 
    description: 'Solve math puzzles to unlock doors and progress!', 
    icon: '🔐', 
    difficulty: 'Medium',
    topics: ['Algebra', 'Geometry', 'Problem Solving'],
    unlimited: true
  },
  { 
    id: 'drag-drop-match', 
    title: 'Drag & Drop Match', 
    description: 'Match equations with answers by dragging and dropping!', 
    icon: '🎯', 
    difficulty: 'Easy',
    topics: ['Equations', 'Fractions', 'Shapes'],
    unlimited: true
  },
  { 
    id: 'number-runner', 
    title: 'Number Runner', 
    description: 'Endless runner! Collect correct numbers, dodge obstacles!', 
    icon: '🏃', 
    difficulty: 'Hard',
    topics: ['Quick Math', 'Reflexes', 'Speed'],
    unlimited: true
  },
];

// Class 4 Math Games - Aligned with curriculum
const CLASS_4_GAMES = [
  { 
    id: 'multiplication-monsters', 
    title: 'Multiplication Monsters', 
    description: 'Master 2-digit multiplication by defeating monsters!', 
    icon: '👾', 
    difficulty: 'Medium',
    topics: ['Multiplication', '2-digit × 2-digit']
  },
  { 
    id: 'division-dash', 
    title: 'Division Dash', 
    description: 'Race through division problems with remainders.', 
    icon: '🏃‍♂️', 
    difficulty: 'Medium',
    topics: ['Division', 'Remainders']
  },
  { 
    id: 'fraction-pizza', 
    title: 'Fraction Pizza', 
    description: 'Learn fractions by building delicious pizzas!', 
    icon: '🍕', 
    difficulty: 'Medium',
    topics: ['Fractions', 'Equivalent Fractions']
  },
  { 
    id: 'time-travel', 
    title: 'Time Travel', 
    description: 'Master time conversion and clock reading.', 
    icon: '⏰', 
    difficulty: 'Medium',
    topics: ['Time', 'Measurement']
  },
  { 
    id: 'coin-quest', 
    title: 'Coin Quest', 
    description: 'Practice money math in a virtual shop!', 
    icon: '🪙', 
    difficulty: 'Medium',
    topics: ['Money', 'Decimals']
  },
  { 
    id: 'geometry-builder', 
    title: 'Geometry Builder', 
    description: 'Build shapes and calculate perimeter & area.', 
    icon: '📏', 
    difficulty: 'Medium',
    topics: ['Geometry', 'Perimeter', 'Area']
  },
  { 
    id: 'decimal-dragons', 
    title: 'Decimal Dragons', 
    description: 'Conquer decimal operations with dragons!', 
    icon: '🐉', 
    difficulty: 'Medium',
    topics: ['Decimals', 'Addition', 'Subtraction']
  },
  { 
    id: 'pattern-master', 
    title: 'Pattern Master', 
    description: 'Complete number patterns and sequences.', 
    icon: '🔢', 
    difficulty: 'Easy',
    topics: ['Patterns', 'Sequences']
  },
  { 
    id: 'number-shooting', 
    title: 'Number Shooting', 
    description: 'Shoot the correct answers in this action game!', 
    icon: '🎯', 
    difficulty: 'Easy',
    topics: ['Addition', 'Subtraction', '4-digit numbers']
  },
  { 
    id: 'word-problem-adventure', 
    title: 'Word Problem Adventure', 
    description: 'Solve real-world math problems in stories.', 
    icon: '📚', 
    difficulty: 'Hard',
    topics: ['Word Problems', 'Mixed Operations']
  }
];

const Games = () => {
  const [selectedClass, setSelectedClass] = React.useState('general');
  const [showSubscriptionModal, setShowSubscriptionModal] = React.useState(false);
  const currentUser = getCurrentUser();
  const isGuest = currentUser.id === -1;
  const hasActivePremium = hasActiveSubscription(currentUser);
  const games = selectedClass === 4 ? CLASS_4_GAMES : selectedClass === 5 ? CLASS_5_GAMES : GENERAL_GAMES;

  // Debug logging for subscription status
  console.log('Games component subscription check:', {
    currentUser,
    isGuest,
    userRole: currentUser?.role,
    userRoleParsed: parseInt(currentUser?.role) || 0,
    subscriptionStatus: currentUser?.subscription?.status,
    hasActivePremium,
    isTestUser: currentUser?.email === 'testuser@example.com' || currentUser?.username === 'testuser@example.com'
  });

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Easy': return 'bg-green-100 text-green-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'Hard': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleGameClick = (game, e) => {
    // If guest user or user without active subscription tries to access grade 4 or 5 games
    if ((isGuest || !hasActivePremium) && (selectedClass === 4 || selectedClass === 5)) {
      e.preventDefault();
      setShowSubscriptionModal(true);
      return false;
    }
    // Allow access to general games for everyone
    return true;
  };

  const SubscriptionModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            {isGuest ? 'Login Required' : 'Premium Subscription Required'}
          </h3>
          <p className="text-gray-600 mb-6">
            {isGuest 
              ? `Grade ${selectedClass} games require an account. Please login or register to access premium content.`
              : `Your premium subscription has expired. Grade ${selectedClass} games require an active subscription.`
            }
          </p>
          
          <div className="space-y-3">
            <Link
              to="/pricing"
              className="block w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all"
              onClick={() => setShowSubscriptionModal(false)}
            >
              {isGuest ? 'View Pricing Plans' : 'Renew Subscription'}
            </Link>
            {isGuest && (
              <Link
                to="/register"
                className="block w-full border border-gray-300 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-50 transition-all"
                onClick={() => setShowSubscriptionModal(false)}
              >
                Create Account
              </Link>
            )}
            <button
              onClick={() => setShowSubscriptionModal(false)}
              className="w-full text-gray-500 py-2 hover:text-gray-700 transition-colors"
            >
              Continue with Free Games
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Math Games
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-6">
            Learn mathematics through fun, interactive games! Each game focuses on specific topics from your curriculum.
          </p>
          
          {/* Class Selector */}
          <div className="flex justify-center gap-4 flex-wrap">
            <button
              onClick={() => setSelectedClass('general')}
              className={`px-8 py-3 rounded-lg font-bold text-lg transition-all ${
                selectedClass === 'general' 
                  ? 'bg-gradient-to-r from-green-600 to-blue-600 text-white shadow-lg scale-105' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              🎮 General Games
            </button>
            <button
              onClick={() => setSelectedClass(4)}
              className={`px-8 py-3 rounded-lg font-bold text-lg transition-all relative ${
                selectedClass === 4 
                  ? 'bg-blue-600 text-white shadow-lg scale-105' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Class 4
              {(isGuest || !hasActivePremium) && <span className="absolute -top-1 -right-1 text-yellow-500">🔒</span>}
            </button>
            <button
              onClick={() => setSelectedClass(5)}
              className={`px-8 py-3 rounded-lg font-bold text-lg transition-all relative ${
                selectedClass === 5 
                  ? 'bg-blue-600 text-white shadow-lg scale-105' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Class 5
              {(isGuest || !hasActivePremium) && <span className="absolute -top-1 -right-1 text-yellow-500">🔒</span>}
            </button>
          </div>
          
          {/* Guest/Expired Notice */}
          {(isGuest || !hasActivePremium) && (selectedClass === 4 || selectedClass === 5) && (
            <div className="mt-6 max-w-2xl mx-auto bg-gradient-to-r from-purple-100 to-blue-100 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center justify-center gap-3">
                <span className="text-2xl">👑</span>
                <div className="text-center">
                  <p className="text-purple-800 font-medium">
                    {isGuest 
                      ? `Grade ${selectedClass} games require a premium subscription`
                      : `Your premium subscription has expired. Grade ${selectedClass} games are now locked.`
                    }
                  </p>
                  <p className="text-purple-600 text-sm">
                    <Link to="/pricing" className="underline hover:text-purple-800">
                      {isGuest ? 'View pricing plans' : 'Renew your subscription'}
                    </Link> or try our free general games!
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Games Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {games.map((game) => {
            const isLocked = (isGuest || !hasActivePremium) && (selectedClass === 4 || selectedClass === 5);
            
            return (
              <div
                key={game.id}
                className={`bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group ${
                  isLocked ? 'opacity-75' : 'hover:scale-105'
                }`}
              >
                {isLocked ? (
                  <div
                    onClick={(e) => handleGameClick(game, e)}
                    className="p-6 cursor-pointer relative"
                  >
                    {/* Lock Overlay */}
                    <div className="absolute top-4 right-4 bg-yellow-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm">
                      🔒
                    </div>
                    
                    {/* Icon and Title */}
                    <div className="flex items-center mb-4">
                      <div className="text-5xl mr-4 grayscale">
                        {game.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-600">
                          {game.title}
                        </h3>
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold mt-1 ${getDifficultyColor(game.difficulty)}`}>
                          {game.difficulty}
                        </span>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-gray-500 mb-4 text-sm">
                      {game.description}
                    </p>

                    {/* Topics */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {game.topics.map((topic, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-gray-100 text-gray-500 rounded-md text-xs font-medium"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>

                    {/* Premium Badge */}
                    <div className="bg-gradient-to-r from-purple-100 to-blue-100 border border-purple-200 rounded-lg p-3 mb-4">
                      <div className="flex items-center justify-between">
                        <span className="text-purple-700 font-medium text-sm">Premium Game</span>
                        <span className="text-purple-600 text-xs">👑</span>
                      </div>
                    </div>

                    {/* Upgrade Button */}
                    <div className="pt-4 border-t border-gray-100">
                      <div className="flex items-center justify-between text-purple-600 font-semibold">
                        <span>Upgrade to Play</span>
                        <span className="text-xl">🔓</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    to={`/games/${game.id}`}
                    className="block p-6"
                  >
                    {/* Icon and Title */}
                    <div className="flex items-center mb-4">
                      <div className="text-5xl mr-4 group-hover:scale-110 transition-transform">
                        {game.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 group-hover:text-primary transition-colors">
                          {game.title}
                        </h3>
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold mt-1 ${getDifficultyColor(game.difficulty)}`}>
                          {game.difficulty}
                        </span>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-gray-600 mb-4 text-sm">
                      {game.description}
                    </p>

                    {/* Topics */}
                    <div className="flex flex-wrap gap-2">
                      {game.unlimited && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded-md text-xs font-bold">
                          ♾️ UNLIMITED
                        </span>
                      )}
                      {game.topics.map((topic, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-medium"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>

                    {/* Play Button */}
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="flex items-center justify-between text-primary font-semibold group-hover:text-primary-dark">
                        <span>Play Now</span>
                        <span className="text-xl group-hover:translate-x-2 transition-transform">→</span>
                      </div>
                    </div>
                  </Link>
                )}
              </div>
            );
          })}
        </div>

        {/* Info Section */}
        <div className="mt-16 max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {selectedClass === 'general' ? 'About General Games' : `About Class ${selectedClass} Math Games`}
            </h2>
            <div className="grid md:grid-cols-2 gap-6 text-gray-600">
              {selectedClass === 'general' ? (
                <>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">♾️ Unlimited Play</h3>
                    <p className="text-sm">Play as much as you want! No limits, no restrictions.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">🎮 For Everyone</h3>
                    <p className="text-sm">Available to all users - no subscription required!</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">🧠 Brain Training</h3>
                    <p className="text-sm">Improve memory, vocabulary, and creative thinking skills.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">🎨 Creative & Fun</h3>
                    <p className="text-sm">Draw, match, and solve puzzles in engaging ways!</p>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">📚 Curriculum Aligned</h3>
                    <p className="text-sm">All games are designed to match Class {selectedClass} math curriculum topics.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">🎮 Learn by Playing</h3>
                    <p className="text-sm">Interactive gameplay makes learning fun and engaging, helping students master concepts through practice.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">📊 Track Progress</h3>
                    <p className="text-sm">Monitor your improvement with scores and achievements as you complete each game.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">🏆 Earn Rewards</h3>
                    <p className="text-sm">Collect points, badges, and climb the leaderboard as you master mathematics!</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Coming Soon Note */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            {selectedClass === 'general' 
              ? '♾️ All general games are unlimited and free for everyone!' 
              : 'More classes and games coming soon! Currently supporting Class 4 & 5.'}
          </p>
        </div>
      </div>
      
      {/* Subscription Modal */}
      {showSubscriptionModal && <SubscriptionModal />}
      
      {/* Interactive Mascot */}
      <InteractiveMascot 
        position="bottom-right"
        onInteraction={(type, data) => console.log('Games mascot interaction:', type, data)}
      />
    </div>
  );
};

export default Games;